<template>
    <div
        class="booking-bar"
        :class="{
            'is-range-mode': isStayExperience,
            'is-single-mode': !isStayExperience,
        }"
    >
        <div class="mobile-search-button" v-show="!showSearch">
            <button @click="toggleSearch" class="btn btn-secondary btn-block">
                Book Now
            </button>
        </div>
        <div class="">
            <Transition name="slideup">
                <div
                    class="row search-row"
                    :class="{ 'is-open': showSearch }"
                    v-show="showSearch"
                >
                    <button class="close" @click="toggleSearch">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            class="size-6"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M6 18 18 6M6 6l12 12"
                            />
                        </svg>
                        Close
                    </button>

                    <!-- Location Dropdown with Default Option -->
                    <div
                        class="col-md-3 location-dropdown"
                        v-if="!initialLocation"
                    >
                        <div class="form-group floating-label">
                            <label
                                :class="{ active: selectedOptions.location }"
                                for="location"
                                >Choose Location</label
                            >
                            <select
                                class="form-control"
                                id="location"
                                v-model="selectedOptions.location"
                                @change="updateExperienceOptions"
                            >
                                <option
                                    v-for="option in locationOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Experience Dropdown -->
                    <div class="col-md-3 experience-dropdown">
                        <div class="form-group floating-label">
                            <label
                                :class="{ active: selectedOptions.experience }"
                                for="experience"
                                >Choose Experience</label
                            >
                            <select
                                class="form-control experience-select"
                                id="experience"
                                v-model="selectedOptions.experience"
                                @change="
                                    onExperienceChange(
                                        selectedOptions.experience
                                    )
                                "
                            >
                                <option
                                    v-for="option in experienceOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                            <div class="arrow"></div>
                        </div>
                    </div>

                    <!-- Start Date and Calendar with Custom Day Content -->
                    <div
                        class="col-md-3 date-dropdown"
                        v-if="showDatesSelection"
                    >
                        <div class="form-group floating-label">
                            <label
                                :class="{
                                    active:
                                        (selectedOptions.dateRange.start &&
                                            selectedOptions.dateRange.end) ||
                                        selectedOptions.singleDate,
                                }"
                                for="startDate"
                            >
                                {{ dateFieldLabel }}
                            </label>
                            <div
                                @click="toggleCalendar"
                                class="form-control"
                                ref="calendarToggle"
                            >
                                <span
                                    v-if="
                                        selectedOptions.dateRange.start ||
                                        selectedOptions.singleDate
                                    "
                                    class="date-range"
                                >
                                    {{ formattedDateRange }}
                                </span>
                                <span v-else>&nbsp;</span>
                            </div>
                        </div>

                        <Transition name="fade">
                            <div
                                class="calendar-popup"
                                v-show="calendarOpen"
                                ref="calendarPopup"
                                :class="calendarPosition"
                            >
                                <v-date-picker
                                    ref="calendar"
                                    v-model="calendarValue"
                                    :mode="
                                        isStayExperience ? 'range' : 'single'
                                    "
                                    :is-range="isStayExperience"
                                    :min-date="minDate"
                                    :max-date="maxDate"
                                    is-expanded
                                    :attributes="atts"
                                    :disabled-dates="disabledDates"
                                    @update:to-page="nextMonth"
                                    @input="onDateSelect"
                                >
                                    <template v-slot:footer>
                                        <button
                                            @click="closeCalendar"
                                            class="closebutton"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke-width="1.5"
                                                stroke="currentColor"
                                                class="size-6"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    d="M6 18 18 6M6 6l12 12"
                                                />
                                            </svg>
                                            Close
                                        </button>
                                    </template>
                                    <template
                                        v-slot:day-content="{
                                            day,
                                            attributes,
                                            dayEvents,
                                        }"
                                    >
                                        <div
                                            class="vc-day-content"
                                            :class="{
                                                dayDisabled: isDisabledDate(
                                                    day.date
                                                ),
                                            }"
                                            v-on="dayEvents"
                                        >
                                            <div :data-date="day.date">
                                                {{ day.day }}
                                            </div>
                                            <div
                                                v-if="loading && !hideRates"
                                                class="vc-day-content__rate loading-icon"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 200 200"
                                                >
                                                    <rect
                                                        fill="#C8C8C8"
                                                        stroke="#C8C8C8"
                                                        stroke-width="15"
                                                        width="30"
                                                        height="30"
                                                        x="25"
                                                        y="85"
                                                    >
                                                        <animate
                                                            attributeName="opacity"
                                                            calcMode="spline"
                                                            dur="2"
                                                            values="1;0;1;"
                                                            keySplines=".5 0 .5 1;.5 0 .5 1"
                                                            repeatCount="indefinite"
                                                            begin="-.4"
                                                        ></animate>
                                                    </rect>
                                                    <rect
                                                        fill="#C8C8C8"
                                                        stroke="#C8C8C8"
                                                        stroke-width="15"
                                                        width="30"
                                                        height="30"
                                                        x="85"
                                                        y="85"
                                                    >
                                                        <animate
                                                            attributeName="opacity"
                                                            calcMode="spline"
                                                            dur="2"
                                                            values="1;0;1;"
                                                            keySplines=".5 0 .5 1;.5 0 .5 1"
                                                            repeatCount="indefinite"
                                                            begin="-.2"
                                                        ></animate>
                                                    </rect>
                                                    <rect
                                                        fill="#C8C8C8"
                                                        stroke="#C8C8C8"
                                                        stroke-width="15"
                                                        width="30"
                                                        height="30"
                                                        x="145"
                                                        y="85"
                                                    >
                                                        <animate
                                                            attributeName="opacity"
                                                            calcMode="spline"
                                                            dur="2"
                                                            values="1;0;1;"
                                                            keySplines=".5 0 .5 1;.5 0 .5 1"
                                                            repeatCount="indefinite"
                                                            begin="0"
                                                        ></animate>
                                                    </rect>
                                                </svg>
                                            </div>
                                            <div
                                                v-else-if="
                                                    (attributes &&
                                                        attributes[0]
                                                            ?.customData
                                                            ?.lowest_rate
                                                            ?.double_rate) ||
                                                    attributes[0]?.customData
                                                        ?.lowest_rate
                                                        ?.single_rate
                                                "
                                                class="vc-day-content__rate"
                                            >
                                                £{{
                                                    selectedOptions.experience ===
                                                    "spa_evening"
                                                        ? attributes[0]
                                                              .customData
                                                              .lowest_rate
                                                              .single_rate
                                                        : attributes[0]
                                                              .customData
                                                              .lowest_rate
                                                              .double_rate
                                                }}
                                            </div>
                                            <div
                                                v-else-if="
                                                    ![
                                                        'treatments',
                                                        'gift_cards',
                                                        'dining',
                                                    ].includes(
                                                        selectedOptions.experience
                                                    ) && !hideRates
                                                "
                                                class="vc-day-content__rate"
                                            >
                                                -
                                            </div>
                                        </div>
                                    </template>
                                </v-date-picker>
                            </div>
                        </Transition>
                    </div>

                    <!-- Guests -->
                    <div
                        class="col-md-3 guests-dropdown"
                        v-if="showGuestSelection"
                    >
                        <div class="form-group floating-label">
                            <label
                                :class="{
                                    active: selectedOptions.guests,
                                }"
                                for="guests"
                                >Guests</label
                            >
                            <div class="guest-selection">
                                <input
                                    type="number"
                                    v-model="selectedOptions.guests"
                                    id="guests"
                                    class="form-control"
                                    readonly
                                />
                                <button
                                    @click="decreaseGuests"
                                    :disabled="selectedOptions.guests <= 1"
                                >
                                    -
                                </button>
                                <button
                                    @click="increaseGuests"
                                    :disabled="selectedOptions.guests >= 6"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Search Button -->
                    <div
                        class="col-md-3 search-button"
                        :class="{ 'mt-auto': showSearch }"
                    >
                        <button
                            @click="search"
                            class="btn btn-secondary btn-block"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </Transition>
        </div>
        <div v-if="showDateRangeError" class="error-message">
            <h3>Sorry,</h3>
            <p>
                Your selection exceeds the maximum stay of 6 nights. Please
                select a shorter date range.
            </p>
            <button class="btn btn-secondary" @click="closeErrorMessage">
                OK
            </button>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
    name: "BookingBar",
    props: {
        initialLocation: {
            type: Number,
            default: 100,
        },
        promoCode: {
            type: String,
            default: null,
        },
        rateCodes: {
            type: [Array, String],
            default: null,
        },
        roomTypes: {
            type: [Array, String],
            default: null,
        },
        hideRates: {
            type: Boolean,
            default: false,
        },
        numberNights: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            locationOptions: [
                {
                    label: "Hoar Cross Hall Spa Hotel (Staffordshire)",
                    value: 100,
                },
                { label: "Eden Hall Spa (Nottinghamshire)", value: 300 },
            ],
            experienceOptions: [],
            selectedOptions: {
                location: 100,
                experience: "stay",
                dateRange: {
                    start: moment().add(7, "days").format("YYYY-MM-DD"),
                    end: moment()
                        .add(7 + this.numberNights, "days")
                        .format("YYYY-MM-DD"),
                },
                singleDate: moment().add(7, "days").format("YYYY-MM-DD"),
                guests: 2,
            },
            calendarOpen: false,
            showSearch: false,
            minDate: moment().format("YYYY-MM-DD"),
            maxDate: moment().add(1, "years").format("YYYY-MM-DD"),
            maxGuests: 6,
            atts: null,
            disabledDates: [],
            loading: false,
            stayType: "stay",
            showDateRangeError: false,
            calendarPosition: "",
        };
    },
    computed: {
        isStayExperience() {
            return this.selectedOptions.experience === "stay";
        },
        dateFieldLabel() {
            return this.isStayExperience ? "Choose Dates" : "Choose Start Date";
        },
        showGuestSelection() {
            return !["gift_cards", "treatments"].includes(
                this.selectedOptions.experience
            );
        },
        showDatesSelection() {
            return !["gift_cards"].includes(this.selectedOptions.experience);
        },
        formattedDateRange() {
            if (this.isStayExperience) {
                const { start, end } = this.selectedOptions.dateRange;
                if (start && end) {
                    return `${moment(start).format("ddd, D MMM")} - ${moment(
                        end
                    ).format("ddd, D MMM")}`;
                }
            } else if (this.selectedOptions.singleDate) {
                return moment(this.selectedOptions.singleDate).format(
                    "ddd, D MMM"
                );
            }
            return "Choose Start Date";
        },
        calendarValue: {
            get() {
                return this.isStayExperience
                    ? this.selectedOptions.dateRange
                    : this.selectedOptions.singleDate || null;
            },
            set(value) {
                if (this.isStayExperience) {
                    this.selectedOptions.dateRange = value;
                } else {
                    this.selectedOptions.singleDate = value;
                }
            },
        },
    },
    methods: {
        increaseGuests() {
            if (this.selectedOptions.guests < 6) {
                this.selectedOptions.guests++;
            }
        },
        decreaseGuests() {
            if (this.selectedOptions.guests > 1) {
                this.selectedOptions.guests--;
            }
        },
        onDateSelect() {
            this.validateDateRange();

            if (
                !this.isStayExperience ||
                (this.calendarValue.start && this.calendarValue.end)
            ) {
                this.closeCalendar();
            }
        },
        validateDateRange() {
            if (!this.isStayExperience) {
                this.selectedOptions.dateRange.end = null;
                return;
            }

            const { start, end } = this.selectedOptions.dateRange || {};
            if (start && end) {
                const nights = moment(end).diff(moment(start), "days");
                if (nights < this.numberNights) {
                    this.selectedOptions.dateRange.end = moment(start)
                        .add(this.numberNights, "days")
                        .format("YYYY-MM-DD");

                    this.$nextTick(() => {
                        this.calendarValue = {
                            ...this.selectedOptions.dateRange,
                        };
                    });
                } else if (nights > 6) {
                    this.showDateRangeError = true;
                    this.selectedOptions.dateRange.end = moment(start)
                        .add(6, "days")
                        .format("YYYY-MM-DD");
                } else {
                    this.showDateRangeError = false;
                }
            } else {
                this.selectedOptions.dateRange.start = moment()
                    .add(7, "days")
                    .format("YYYY-MM-DD");
                this.selectedOptions.dateRange.end = moment(
                    this.selectedOptions.dateRange.start
                )
                    .add(this.numberNights, "days")
                    .format("YYYY-MM-DD");
            }
            this.closeCalendar();
        },
        onExperienceChange(newExperience) {
            this.stayType = newExperience === "stay" ? "stay" : newExperience;

            this.selectedOptions.guests = ["treatments", "gift_cards"].includes(
                newExperience
            )
                ? 1
                : 2;

            if (newExperience !== "stay") {
                // Clear range and set single date for non-stay experiences
                this.selectedOptions.dateRange = { start: null, end: null };
                this.selectedOptions.singleDate = moment()
                    .add(7, "days")
                    .format("YYYY-MM-DD");
            } else {
                // Set range dates for stay experiences if they don't exist
                if (!this.selectedOptions.dateRange.start) {
                    this.selectedOptions.dateRange.start = moment()
                        .add(7, "days")
                        .format("YYYY-MM-DD");
                }
                if (!this.selectedOptions.dateRange.end) {
                    this.selectedOptions.dateRange.end = moment(
                        this.selectedOptions.dateRange.start
                    )
                        .add(this.numberNights, "days")
                        .format("YYYY-MM-DD");
                }
            }

            if (
                !["treatments", "gift_cards", "dining"].includes(newExperience)
            ) {
                this.refetchRates();
            } else {
                this.atts = [];
                this.disabledDates = [];
            }
        },
        closeErrorMessage() {
            this.showDateRangeError = false;
        },
        getVenueId() {
            const location = this.selectedOptions.location;
            const experience = this.selectedOptions.experience;

            return location === 100 &&
                ["spa_day", "spa_evening"].includes(experience)
                ? 200
                : location;
        },

        refetchRates() {
            const start = moment().startOf("month").format("YYYY-MM-DD");
            const end = moment().endOf("month").format("YYYY-MM-DD");
            const stayType = this.stayType;
            this.fetchCalendarRates(start, end, stayType);
        },

        isDisabledDate(date) {
            return this.disabledDates.some((range) => {
                const start = range.start ? new Date(range.start) : null;
                const end = range.end ? new Date(range.end) : null;

                if (start && end) {
                    return date >= start && date <= end;
                } else if (start) {
                    return date >= start;
                } else if (end) {
                    return date <= end;
                }
                return false;
            });
        },

        updateExperienceOptions() {
            const allOptions = {
                hoar_cross_hall: [
                    { label: "Spa Stay", value: "stay" },
                    { label: "Spa Day", value: "spa_day" },
                    { label: "Spa Evening (Twilight)", value: "spa_evening" },
                    { label: "Hotel Stay", value: "hotel_stay" },
                    { label: "Gift Cards", value: "gift_cards" },
                    { label: "Treatments", value: "treatments" },
                    { label: "Afternoon Tea", value: "dining" },
                ],
                eden_hall: [
                    { label: "Spa Day", value: "spa_day" },
                    { label: "Spa Evening (Twilight)", value: "spa_evening" },
                    { label: "Gift Cards", value: "gift_cards" },
                    { label: "Treatments", value: "treatments" },
                    { label: "Afternoon Tea", value: "dining" },
                ],
            };

            this.experienceOptions =
                this.selectedOptions.location === 100
                    ? allOptions.hoar_cross_hall
                    : allOptions.eden_hall;

            this.stayType = this.selectedOptions.experience;
        },

        fetchCalendarRates(start, end, stayType) {
            if (this.hideRates) {
                this.atts = [];
                this.disabledDates = [];
                return;
            }

            if (
                !start ||
                !end ||
                ["treatments", "gift_cards", "dining"].includes(this.stayType)
            ) {
                this.atts = [];
                return;
            }

            this.loading = true;

            const adjustedStayType = this.selectedOptions.experience;

            const extendedEnd = moment(end)
                .add(1, "months")
                .format("YYYY-MM-DD");

            const requestData = {
                venue_id: this.getVenueId(),
                start_date: start,
                end_date: extendedEnd,
                stay_type: adjustedStayType,
                min_length: adjustedStayType === "stay" ? this.numberNights : 0,
            };

            if (this.promoCode) requestData.promo_code = this.promoCode;

            if (this.rateCodes) {
                requestData.rate_codes = Array.isArray(this.rateCodes)
                    ? this.rateCodes
                    : this.rateCodes.split(",").map((code) => code.trim());
            }

            if (this.roomTypes) {
                requestData.room_types = Array.isArray(this.roomTypes)
                    ? this.roomTypes
                    : this.roomTypes.split(",").map((type) => type.trim());
            }

            axios
                .post(
                    "https://api.baronseden.com/api/calendar/rates",
                    requestData
                )
                .then((response) => {
                    let rates = response.data.data;

                    const validStays = [];
                    rates.forEach((rate) => {
                        const startDate = moment(rate.date);
                        const endDate = moment(rate.date).add(
                            this.numberNights - 1,
                            "days"
                        );

                        const stayRates = rates.filter((r) =>
                            moment(r.date).isBetween(
                                startDate,
                                endDate,
                                null,
                                "[]"
                            )
                        );

                        const roomTypes = [
                            ...new Set(stayRates.map((r) => r.room_type)),
                        ];

                        if (roomTypes.length === 1) {
                            validStays.push(rate);
                        }
                    });

                    this.atts = validStays.map((rate) => ({
                        dates: rate.date,
                        customData: rate,
                    }));

                    this.disabledDates = validStays
                        .filter((rate) => {
                            const startDate = moment(rate.date);
                            const endDate = moment(rate.date).add(
                                this.numberNights - 1,
                                "days"
                            );

                            const stayRates = rates.filter((r) =>
                                moment(r.date).isBetween(
                                    startDate,
                                    endDate,
                                    null,
                                    "[]"
                                )
                            );

                            const roomTypes = [
                                ...new Set(stayRates.map((r) => r.room_type)),
                            ];

                            return roomTypes.length !== 1;
                        })
                        .map((rate) => ({
                            start: new Date(rate.date),
                            end: new Date(rate.date),
                        }));

                    setTimeout(() => {
                        this.loading = false;
                    }, 500);
                })
                .catch((error) => {
                    console.error("Error fetching rates:", error);
                    setTimeout(() => {
                        this.loading = false;
                    }, 500);
                });
        },
        toggleCalendar() {
            const toggleButton = this.$refs.calendarToggle;

            if (!this.calendarOpen && toggleButton) {
                const viewportHeight = window.innerHeight;
                const toggleRect = toggleButton.getBoundingClientRect();
                const estimatedCalendarHeight = 466;

                this.calendarPosition =
                    viewportHeight - toggleRect.bottom < estimatedCalendarHeight
                        ? "position-top"
                        : "";
            }

            this.calendarOpen = !this.calendarOpen;

            if (this.calendarOpen) {
                setTimeout(() => {
                    this.updateCalendarPosition();
                }, 50);

                window.addEventListener("resize", this.updateCalendarPosition);
            } else {
                window.removeEventListener(
                    "resize",
                    this.updateCalendarPosition
                );
            }

            this.refetchRates();
        },
        updateCalendarPosition() {
            const calendarPopup = this.$refs.calendarPopup;
            const toggleButton = this.$refs.calendarToggle;

            if (calendarPopup && toggleButton) {
                const viewportHeight = window.innerHeight;
                const popupRect = calendarPopup.getBoundingClientRect();
                const toggleRect = toggleButton.getBoundingClientRect();

                this.calendarPosition =
                    viewportHeight - toggleRect.bottom < popupRect.height
                        ? "position-top"
                        : "";

                if (
                    spaceBelow < popupRect.height &&
                    spaceAbove > popupRect.height
                ) {
                    this.calendarPosition = "position-top";
                } else {
                    this.calendarPosition = "";
                }
            }
        },
        closeCalendar() {
            this.calendarOpen = false;
            window.removeEventListener("resize", this.updateCalendarPosition);
        },
        toggleSearch() {
            this.showSearch = !this.showSearch;
        },
        nextMonth({ month, year }) {
            const start = moment([year, month - 1])
                .startOf("month")
                .format("YYYY-MM-DD");
            const end = moment([year, month - 1])
                .endOf("month")
                .format("YYYY-MM-DD");
            this.fetchCalendarRates(start, end, this.stayType);
        },
        search() {
            const { location, experience, dateRange, singleDate, guests } =
                this.selectedOptions;
            const venue = location === 100 ? "hoar-cross-hall" : "eden-hall";
            const bookingType = "build";

            let queryParams = "";

            if (this.rateCodes) {
                queryParams = "?rate=" + this.rateCodes.split(" ").join("+");
            }
            if (this.promoCode) {
                queryParams = "?promo=" + this.promoCode;
            }

            const formattedStartDate = this.isStayExperience
                ? dateRange.start
                    ? moment(dateRange.start).format("YYYY-MM-DD")
                    : ""
                : singleDate
                ? moment(singleDate).format("YYYY-MM-DD")
                : "";

            const formattedEndDate =
                this.isStayExperience && dateRange.end
                    ? moment(dateRange.end).format("YYYY-MM-DD")
                    : "";

            const numberOfNights =
                this.isStayExperience && formattedStartDate && formattedEndDate
                    ? moment(formattedEndDate).diff(
                          moment(formattedStartDate),
                          "days"
                      )
                    : 0;

            let url = `https://book.baronseden.com/book/${bookingType}/${venue}/${experience}/${formattedStartDate}/${numberOfNights}/${guests}`;
            url += queryParams;

            window.open(url, "_blank");
        },
    },
    mounted() {
        const url = window.location.href;

        if (url.includes("hoar-cross-hall")) {
            this.selectedOptions.location = 100;
            this.selectedOptions.experience = "stay";
        } else if (url.includes("eden-hall")) {
            this.selectedOptions.location = 300;
            this.selectedOptions.experience = "spa_day";
        } else {
            this.selectedOptions.location = 100;
            this.selectedOptions.experience = "stay";
        }

        this.updateExperienceOptions();

        if (url.includes("twilight")) {
            this.selectedOptions.experience = "spa_evening";
        } else if (url.includes("/day-spa-packages/")) {
            this.selectedOptions.experience = "spa_day";
        } else if (url.includes("/overnight-experiences/")) {
            this.selectedOptions.experience = "stay";
        } else if (url.includes("/hotel-stay/")) {
            this.selectedOptions.experience = "hotel_stay";
        }

        this.stayType = this.selectedOptions.experience;

        if (!this.hideRates) {
            const start = this.minDate;
            const end = moment(start)
                .add(1, "month")
                .endOf("month")
                .format("YYYY-MM-DD");
            this.fetchCalendarRates(start, end, this.stayType);
        }
    },
    watch: {
        initialLocation(newLocation) {
            this.selectedOptions.location = newLocation;
            this.updateExperienceOptions();
        },
        "selectedOptions.guests": function (newValue) {
            if (newValue > 6) this.selectedOptions.guests = 6;
            if (newValue < 1) this.selectedOptions.guests = 1;
        },
        numberNights(newVal) {
            this.selectedOptions.dateRange.end = moment(
                this.selectedOptions.dateRange.start
            )
                .add(newVal, "days")
                .format("YYYY-MM-DD");

            this.$nextTick(() => {
                this.calendarValue = { ...this.selectedOptions.dateRange };
            });

            this.refetchRates();
        },
    },
};
</script>

<style scoped lang="scss">
.closebutton {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    border: none;
    background: transparent;
    padding: 0;
    margin-bottom: 20px;
    font-size: 12px;

    svg {
        margin-right: 5px;
        width: 20px;
        height: 20px;
    }
}

.guest-selection {
    position: relative;

    button {
        position: absolute;
        top: 0;
        right: 0;
        width: 30px;
        height: 100%;
        background: none;
        border: none;
        color: #000;
        font-size: 1.5rem;
        cursor: pointer;

        &:first-of-type {
            right: 30px;
        }
    }
}
</style>
